/**
 * Material demand model.
 *
 * Answers "what does this city cost, all the way down to elements?" Used by
 * world generation's solvability check (spec §6 rule 5), and later by the build
 * queue's committed-cost display (spec §9).
 *
 * The chain is goods -> elements, with one wrinkle: topsoil consumes fertilizer,
 * which is itself a good. Expansion is therefore iterative rather than a single
 * pass. It is written generically so that adding another good-consuming recipe
 * later does not need new code here.
 */

import {
  BUILDINGS,
  ELEMENT_IDS,
  GOODS,
  GOOD_IDS,
  MINERALS,
  MINERAL_IDS,
  type BuildingId,
  type ElementId,
  type GoodId,
  type MineralId,
} from './data';

export type GoodTally = Partial<Record<GoodId, number>>;
export type ElementTally = Record<ElementId, number>;

function zeroElements(): ElementTally {
  const out = {} as ElementTally;
  for (const id of ELEMENT_IDS) out[id] = 0;
  return out;
}

/** Total goods cost of a building mix. */
export function goodsForBuildings(mix: Partial<Record<BuildingId, number>>): GoodTally {
  const totals: GoodTally = {};
  for (const buildingId of Object.keys(mix).sort() as BuildingId[]) {
    const count = mix[buildingId] ?? 0;
    const cost = BUILDINGS[buildingId].cost;
    for (const goodId of GOOD_IDS) {
      const per = cost[goodId];
      if (per === undefined) continue;
      totals[goodId] = (totals[goodId] ?? 0) + per * count;
    }
  }
  return totals;
}

/**
 * Expand a goods tally so that goods consumed by other goods' recipes are
 * included. Returns gross goods production required, not net.
 */
export function expandGoodIngredients(goods: GoodTally): GoodTally {
  const totals: GoodTally = { ...goods };

  // One pass per possible dependency depth. GOOD_IDS.length is a hard bound on
  // chain length in an acyclic recipe graph, and doubles as a cycle guard.
  for (let depth = 0; depth < GOOD_IDS.length; depth++) {
    let changed = false;
    for (const goodId of GOOD_IDS) {
      const quantity = totals[goodId] ?? 0;
      if (quantity === 0) continue;
      const ingredients = GOODS[goodId].ingredients;
      if (ingredients === undefined) continue;

      for (const ingredientId of GOOD_IDS) {
        const per = ingredients[ingredientId];
        if (per === undefined) continue;
        const owed = per * quantity;
        const already = totals[ingredientId] ?? 0;
        // Only the amount not yet accounted for by this producer.
        if (owed > 0 && already < owed) {
          totals[ingredientId] = owed;
          changed = true;
        }
      }
    }
    if (!changed) break;
  }

  return totals;
}

/** Elements consumed by producing a goods tally, ingredients included. */
export function elementsForGoods(goods: GoodTally): ElementTally {
  const expanded = expandGoodIngredients(goods);
  const totals = zeroElements();

  for (const goodId of GOOD_IDS) {
    const quantity = expanded[goodId] ?? 0;
    if (quantity === 0) continue;
    const recipe = GOODS[goodId].recipe;
    for (const elementId of ELEMENT_IDS) {
      const per = recipe[elementId];
      if (per === undefined) continue;
      totals[elementId] += per * quantity;
    }
  }

  return totals;
}

/** Elements obtainable by fully dissociating a mineral tally. */
export function elementsFromMinerals(minerals: Record<MineralId, number>): ElementTally {
  const totals = zeroElements();
  for (const mineralId of MINERAL_IDS) {
    const units = minerals[mineralId];
    if (units === 0) continue;
    const yields = MINERALS[mineralId].yields;
    for (const elementId of ELEMENT_IDS) {
      const per = yields[elementId];
      if (per === undefined) continue;
      totals[elementId] += per * units;
    }
  }
  return totals;
}

/**
 * The city a seed must be capable of supporting to count as solvable.
 *
 * Not the full 616 plots: spec §6 sizes total supply at 1.7-2.1x demand, so
 * requiring the whole grid would reject seeds the spec intends to accept. This
 * is a substantial, mixed city — roughly a third of the grid — which is the
 * threshold below which a run is not worth playing.
 */
export const VIABLE_CITY_MIX: Partial<Record<BuildingId, number>> = {
  habitation: 60,
  farm: 40,
  waterPlant: 20,
  powerPlant: 30,
  factoryDistrict: 30,
  recreation: 20,
};

/**
 * Element demand for a viable city. Nitrogen is excluded: spec §4.2 draws it
 * from the atmosphere, so it is energy-gated rather than deposit-gated and no
 * amount of mining affects it.
 */
export function viableCityElementDemand(): ElementTally {
  const demand = elementsForGoods(goodsForBuildings(VIABLE_CITY_MIX));
  demand.N = 0;
  return demand;
}

/**
 * The mineral mix an area needs, as proportions summing to 10,000 basis points.
 *
 * Solves the question spec §6 never asks: not "how much material", but "of
 * WHAT". §6 generates eight minerals from one shared distribution, so the ground
 * supplies whatever the noise happens to produce. Measured, that meant the
 * single-source elements ran out while the abundant ones piled up unused —
 * silicon, carbon, copper and potassium at zero, with 169,000 oxygen and 54,000
 * hydrogen sitting idle, because Si comes only from quartz, C only from
 * carbonate, Cu only from chalcopyrite and K only from sylvite.
 *
 * Worked backwards from element demand, hardest-constrained first. Each mineral
 * is claimed for the element only it can supply, then the multi-source elements
 * are topped up from whatever that already provides. See SPEC-ERRATA.md E19.
 */
export function targetMineralShareBp(): Record<MineralId, number> {
  const demand = viableCityElementDemand();
  const need = {} as Record<MineralId, number>;
  for (const id of MINERAL_IDS) need[id] = 0;

  // --- single-source elements: no choice about where these come from ---
  need.quartz = demand.Si; // 1 Si per unit
  need.carbonate = demand.C; // 1 C per unit
  need.chalcopyrite = demand.Cu; // 1 Cu per unit
  need.sylvite = demand.K; // 1 K per unit
  need.apatite = Math.ceil(demand.P / 3); // 3 P per unit
  need.bauxite = Math.ceil(demand.Al / 2); // 2 Al per unit

  // --- multi-source elements: credit what the above already yields ---
  const suppliedSoFar = elementsFromMinerals(need);

  // Iron comes from hematite and, incidentally, chalcopyrite.
  const ironShort = Math.max(0, demand.Fe - suppliedSoFar.Fe);
  need.hematite = Math.ceil(ironShort / 2); // 2 Fe per unit

  // Hydrogen from bauxite (4) and water ice (2).
  const hydrogenShort = Math.max(0, demand.H - suppliedSoFar.H);
  need.waterIce = Math.ceil(hydrogenShort / 2);

  // Calcium tops up through carbonate, which is cheap and also carries oxygen.
  const withRock = elementsFromMinerals(need);
  const calciumShort = Math.max(0, demand.Ca - withRock.Ca);
  need.carbonate += calciumShort; // 1 Ca per unit

  // Oxygen last: almost everything above carries it, so it is rarely binding.
  const withCalcium = elementsFromMinerals(need);
  const oxygenShort = Math.max(0, demand.O - withCalcium.O);
  need.quartz += Math.ceil(oxygenShort / 2); // 2 O per unit, and more Si is welcome

  let total = 0;
  for (const id of MINERAL_IDS) total += need[id];
  if (total <= 0) {
    const even = {} as Record<MineralId, number>;
    for (const id of MINERAL_IDS) even[id] = Math.floor(10000 / MINERAL_IDS.length);
    return even;
  }

  const shares = {} as Record<MineralId, number>;
  for (const id of MINERAL_IDS) shares[id] = Math.round((need[id] * 10000) / total);
  return shares;
}
